import { DesignDialog as Dialog } from "../../design/DesignDialog.js";
import { useState } from "react";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import type { OperatorNodeProps } from "./OperatorNode";
import type { Port } from "../../../../util/src/blueprint/types";
import {
  OBJECT_FIELD_TYPES,
  OBJECT_TYPE_SOURCE_PREFIX,
  SCENE_OBJECT_BASE_SOURCES,
  isObjectFieldKey,
  sceneObjectTypeError,
} from "../../../../util/src/layout/scene-object-types";
import { useSceneObjectTypes } from "../SceneObjectTypesContext";
import { PortHandle } from "../PortHandle";
import {
  TerminalPortContextMenu,
  type TerminalPortContextMenuState,
} from "../controls/TerminalPortContextMenu";
import dico from "../../../../util/src/language/dico";
import { BehaviorNodeCard } from "../BehaviorNodeCard.js";

export function ObjectDefinitionNode(props: OperatorNodeProps) {
  const {
    node,
    setPortsMap,
    onPortMouseDown,
    onPortMouseUp,
    isPortConnected,
    onPortValueChange,
    onObjectPortsChange,
  } = props;
  const catalog = useSceneObjectTypes();
  const labels = dico().behavior.sceneCatalog.objectEditor;
  const common = dico().behavior.sceneCatalog.functionGraph;
  const types = dico().behavior.nodeList.inputTypes as Record<string, string>;
  const sources = dico().licensedPortal.layoutEditor.dataSources as Record<
    string,
    string
  >;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [type, setType] = useState<string | null>(null);
  const [key, setKey] = useState("");
  const [mode, setMode] = useState("field");
  const [objectTypeId, setObjectTypeId] = useState("");
  const [portMenu, setPortMenu] = useState<TerminalPortContextMenuState | null>(
    null,
  );
  const close = () => {
    setAnchor(null);
    setType(null);
    setKey("");
    setMode("field");
    setObjectTypeId("");
  };
  const customTypes = (catalog?.objectTypes ?? []).filter((entry) => {
    if (node.type !== "operator" || entry.id === node.objectTypeId)
      return false;
    // Remove indirect self references as well as the current type.
    const self = catalog?.objectTypes?.find((t) => t.id === node.objectTypeId);
    return (
      !self ||
      !sceneObjectTypeError(
        { ...self, baseType: entry.id },
        {
          ...catalog!,
          objectTypes: catalog!.objectTypes!.map((t) =>
            t.id === self.id ? { ...self, baseType: entry.id } : t,
          ),
        },
      )
    );
  });
  const options = [
    ...SCENE_OBJECT_BASE_SOURCES.map((source) => ({
      id: OBJECT_TYPE_SOURCE_PREFIX + source,
      label: labels.nativeTypes[source] ?? sources[source] ?? source,
    })),
    ...customTypes.map((entry) => ({ id: entry.id, label: entry.name })),
  ];
  const extension = type === "object" && mode === "extension";
  const keyValid =
    isObjectFieldKey(key) && !node.ports.some((p) => p.id === "field:" + key);
  const canAdd =
    type &&
    (extension
      ? objectTypeId &&
        !node.ports.some(
          (p) =>
            (p.id === "base" || p.id.startsWith("extension:")) &&
            p.objectTypeId === objectTypeId,
        )
      : keyValid);
  const add = () => {
    if (!canAdd || !type) return;
    const id = extension
      ? node.ports.some((p) => p.id === "base")
        ? "extension:" + crypto.randomUUID()
        : "base"
      : "field:" + key;
    const port: Port = {
      id,
      label: extension
        ? (options.find((o) => o.id === objectTypeId)?.label ?? objectTypeId)
        : key,
      direction: "input",
      valueType: type,
      ...((type === "object" || type === "list") && objectTypeId
        ? { objectTypeId }
        : {}),
    };
    onObjectPortsChange?.(node.id, [
      ...node.ports.filter((p) => p.direction === "input"),
      port,
      ...node.ports.filter((p) => p.direction === "output"),
    ]);
    close();
  };
  const contextMenu = (event: React.MouseEvent, port: Port) => {
    if (!onObjectPortsChange || port.direction !== "input") return;
    event.preventDefault();
    event.stopPropagation();
    setPortMenu({
      anchor: event.currentTarget as HTMLElement,
      portId: port.id,
      label: port.label,
    });
  };
  return (
    <BehaviorNodeCard
      minWidth={280}
      header={labels.definition}
      headerBackground="linear-gradient(180deg, #6ee7b7 0%, #059669 55%, #047857 100%)"
    >
      <div onMouseDown={(event) => event.stopPropagation()}>
        {node.ports.map((port) => (
          <div
            key={port.id}
            style={{
              padding: "4px 8px",
              display: "flex",
              justifyContent:
                port.direction === "output" ? "flex-end" : "flex-start",
              alignItems: "center",
              minHeight: 28,
            }}
            onContextMenu={(event) => contextMenu(event, port)}
          >
            {(port.id === "base" || port.id.startsWith("extension:")) && (
              <span style={{ color: "#15803d", fontSize: 10, marginRight: 8 }}>
                {labels.extension}
              </span>
            )}
            <PortHandle
              node={node}
              port={port}
              setPortsMap={setPortsMap}
              onMouseDown={() => onPortMouseDown?.(node.id, port)}
              onMouseUp={() => onPortMouseUp?.(node.id, port)}
              isConnected={isPortConnected?.(node.id, port.id) ?? false}
              showLabel
              showValueInput={
                port.id.startsWith("field:") && port.valueType !== "object"
              }
              onValueChange={(value) =>
                onPortValueChange?.(node.id, port.id, value)
              }
              onContextMenu={(event) => contextMenu(event, port)}
            />
          </div>
        ))}
        {onObjectPortsChange && (
          <IconButton
            size="small"
            aria-label={labels.addField}
            sx={{ ml: 1, mb: 1 }}
            onClick={(event) => {
              event.stopPropagation();
              setAnchor(event.currentTarget);
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        )}
      </div>
      <Menu
        onMouseDown={(event) => event.stopPropagation()}
        anchorEl={anchor}
        open={Boolean(anchor) && type === null}
        onClose={close}
      >
        {OBJECT_FIELD_TYPES.map((value) => (
          <MenuItem key={value} onClick={() => setType(value)}>
            {types[value] ?? value}
          </MenuItem>
        ))}
      </Menu>
      <Dialog
        open={type !== null}
        onClose={close}
        maxWidth="xs"
        fullWidth
        onMouseDown={(event) => event.stopPropagation()}
      >
        <DialogTitle>{labels.addField}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {type === "object" && (
              <TextField
                select
                label={labels.objectMode}
                value={mode}
                onChange={(event) => {
                  setMode(event.target.value);
                  setObjectTypeId("");
                }}
              >
                <MenuItem value="field">{labels.objectField}</MenuItem>
                <MenuItem value="extension">{labels.extension}</MenuItem>
              </TextField>
            )}
            {(type === "object" || type === "list") && (
              <TextField
                select
                label={extension ? labels.extendType : labels.valueType}
                value={objectTypeId}
                onChange={(event) => setObjectTypeId(event.target.value)}
              >
                {!extension && <MenuItem value="">{labels.untyped}</MenuItem>}
                {options.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {!extension && (
              <TextField
                autoFocus
                label={labels.key}
                value={key}
                onChange={(event) => setKey(event.target.value)}
                error={!!key && !keyValid}
                helperText={key && !keyValid ? labels.errors.field : undefined}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    add();
                  }
                }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{common.cancel}</Button>
          <Button variant="contained" disabled={!canAdd} onClick={add}>
            {common.confirmAddPort}
          </Button>
        </DialogActions>
      </Dialog>
      <TerminalPortContextMenu
        validateLabel={(id, name) =>
          id.startsWith("field:") &&
          (!isObjectFieldKey(name) ||
            node.ports.some((p) => p.id !== id && p.id === "field:" + name))
            ? labels.errors.field
            : undefined
        }
        portMenu={portMenu}
        labels={common}
        onClose={() => setPortMenu(null)}
        onRename={(id, name) => {
          const isField = id.startsWith("field:");
          if (
            isField &&
            (!isObjectFieldKey(name) ||
              node.ports.some((p) => p.id !== id && p.id === "field:" + name))
          )
            return;
          const nextId = isField ? "field:" + name : id;
          onObjectPortsChange?.(
            node.id,
            node.ports.map((p) =>
              p.id === id ? { ...p, id: nextId, label: name } : p,
            ),
            { [id]: nextId },
          );
        }}
        onRemove={(id) =>
          onObjectPortsChange?.(
            node.id,
            node.ports.filter((p) => p.id !== id),
          )
        }
      />
    </BehaviorNodeCard>
  );
}
